import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, testimonials } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.slug === id);
  const [testimonialIdx, setTestimonialIdx] = useState(0);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要找的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 4);

  const t = testimonials[testimonialIdx % testimonials.length];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - ${comic.category}韩国漫画`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2026-01-15',
    dateModified: comic.updateTime,
    inLanguage: 'zh-CN',
    publisher: {
      '@type': 'Organization',
      name: '36漫画网',
      url: 'http://jhequip.com/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://jhequip.com/article/${comic.slug}`,
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: comic.category, href: `/category/${comic.category === '都市' ? 'dushi' : comic.category === '恋爱' ? 'lianai' : comic.category === '奇幻' ? 'qihuan' : comic.category === '治愈' ? 'zhiyu' : comic.category === '悬疑' ? 'xuanyi' : comic.category === '校园' ? 'xiaoyuan' : comic.category === '美食' ? 'meishi' : comic.category === '穿越' ? 'chuanyue' : comic.category === '恐怖' ? 'kongbu' : comic.category === '体育' ? 'tiyu' : 'lishi'}` },
    { label: comic.title },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <Navbar />

      <main className="pt-16">
        <section className="bg-background-50">
          <div className="mx-auto px-4 md:px-6 pt-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </section>

        <section className="py-10 md:py-16 bg-background-50">
          <div className="mx-auto px-4 md:px-6 max-w-5xl">
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="lg:w-80 flex-shrink-0">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden sticky top-24">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 韩国漫画无删减`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {comic.category}
                  </span>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                    comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-600'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-2">
                  {comic.title}
                </h1>
                <p className="text-foreground-500 text-sm mb-4">
                  作者：<strong>{comic.author}</strong> · {comic.chapters}话 · 更新于 {comic.updateTime}
                </p>

                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center gap-1.5">
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className={`text-sm ${i < Math.floor(comic.rating) ? 'ri-star-fill text-accent-400' : 'ri-star-line text-foreground-300'}`}></i>
                        </div>
                      ))}
                    </div>
                    <span className="text-foreground-900 font-bold text-lg">{comic.rating}</span>
                    <span className="text-foreground-400 text-xs">({comic.ratingCount.toLocaleString()}人评价)</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-xl p-6 mb-8">
                  <h2 className="text-foreground-900 font-semibold text-sm mb-3">作品简介</h2>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    {comic.detailedDescription}
                  </p>
                </div>

                <Link
                  to="/category/all"
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-book-open-line"></i> 开始阅读
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100/70">
          <div className="mx-auto px-4 md:px-6 max-w-3xl">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
                读者
                <span className="font-serif italic font-normal text-foreground-400">评价</span>
              </h2>
            </div>

            <div className="relative bg-background-50 rounded-2xl p-6 md:p-10">
              <div className="flex items-center gap-2 mb-6">
                <div className="flex gap-0.5">
                  {[...Array(t.rating)].map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-400 text-sm"></i>
                    </div>
                  ))}
                </div>
                <span className="px-2.5 py-0.5 bg-background-900 text-white text-xs font-medium rounded-full whitespace-nowrap">
                  {t.rating}.0
                </span>
              </div>
              <blockquote className="text-foreground-700 text-base md:text-lg leading-relaxed mb-8 font-serif italic">
                &ldquo;{t.text}&rdquo;
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                  <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="text-foreground-900 font-medium text-sm">{t.name}</div>
                  <div className="text-foreground-400 text-xs">{t.role}</div>
                </div>
              </div>
              <div className="absolute bottom-6 right-6 md:bottom-10 md:right-10 flex gap-2">
                <button
                  onClick={() => setTestimonialIdx((p) => (p === 0 ? testimonials.length - 1 : p - 1))}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-300 bg-background-50 hover:bg-background-100 text-foreground-600 transition-colors cursor-pointer"
                  aria-label="上一条评价"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                <button
                  onClick={() => setTestimonialIdx((p) => (p === testimonials.length - 1 ? 0 : p + 1))}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-900 hover:bg-background-800 text-white transition-colors cursor-pointer"
                  aria-label="下一条评价"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8">
              同类
              <span className="text-foreground-400 italic font-normal">推荐</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {relatedComics.map((c) => (
                <Link key={c.id} to={`/article/${c.slug}`} className="group cursor-pointer">
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3">
                    <img
                      src={c.coverImage}
                      alt={c.title}
                      title={`${c.title} - ${c.category}韩国漫画推荐`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 bg-accent-500 text-white text-[10px] font-medium rounded-full whitespace-nowrap">
                        {c.rating}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-foreground-900 font-medium text-sm mb-1 line-clamp-1">{c.title}</h3>
                  <p className="text-foreground-500 text-xs">{c.author}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="mx-auto px-4 md:px-6">
            <div className="relative rounded-3xl overflow-hidden">
              <div className="aspect-[21/9] md:aspect-[3/1]">
                <img
                  src={comic.bannerImage}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/20 flex items-center">
                <div className="px-6 md:px-12 py-10 max-w-xl">
                  <h2 className="text-2xl md:text-4xl font-bold font-heading text-white mb-3 leading-tight">
                    开始阅读
                    <span className="block text-accent-400 italic font-normal">{comic.title}</span>
                  </h2>
                  <p className="text-white/70 text-sm md:text-base mb-6 leading-relaxed">
                    下拉式阅读体验，无删减完整内容，立即开始您的韩漫之旅
                  </p>
                  <Link
                    to="/category/all"
                    className="inline-flex items-center gap-2 px-7 py-3 bg-background-950 text-white text-sm font-medium rounded-full hover:bg-background-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    立即阅读 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}